/**
 * @Author: AI Assistant
 * @Date: 2026-01-13
 * @Description: API相关接口 - 管理所有API页面相关的请求和类型定义
 */
import { http } from '~/utils/request'
import { showLoginModal } from '~/utils/emitter'
import { noticeMessagePopup } from '~/utils/common'
import { LOGIN_ENTRY_ENABLED } from '~/utils/feature-flags'

// ==================== 辅助函数 ====================

/**
 * 处理需要登录验证的接口响应
 * 当返回 1005, 4001, 4002, 5005 状态码时，触发登录弹窗
 * @param promise - API请求的Promise
 * @returns Promise
 */
const handleAuthRequired = async <T>(promise: Promise<any>): Promise<T> => {
    try {
        const response = await promise

        // 检查是否为需要登录的状态码
        if (
            response?.status &&
            [1005, 4001, 4002, 5005].includes(response.status)
        ) {
            if (import.meta.client) {
                if (LOGIN_ENTRY_ENABLED) {
                    showLoginModal()
                } else {
                    noticeMessagePopup(
                        response.desc || response.message || '您当前未登录'
                    )
                }
            }
        }

        return response
    } catch (error) {
        throw error
    }
}

// ==================== 类型定义 ====================

/**
 * 历史记录状态类型
 */
export type HistoryRecordStatus = 'processing' | 'succeed' | 'failed'

/**
 * 灵感作品项接口
 */
export interface InspirationWorkItem {
    /** 作品ID */
    id: number
    /** 关联历史记录ID */
    relate_his_id: number
    /** 类型 */
    type: 'TEXT_TO_VIDEO' | 'IMG_TO_VIDEO' | 'IMG_TO_IMG' | string
    /** 类型名称 */
    type_name: string
    /** 用户信息 */
    user: {
        user_name: string
        avatar: string
    }
    /** 参数信息 */
    param: {
        aspect_ratio: string
        model: string
        prompt: string
        output_num: number
        pic_urls?: string[]
        resolution: string
        t: number
        [key: string]: any
    }
    /** 点赞数 */
    likes_num: number
    /** 是否已点赞 */
    has_like: boolean
    /** 输出文件URL */
    output: string
    /** 带水印的输出文件URL */
    output_with_watermark: string
    /** 创建时间 */
    create_time: number
    /** 状态 */
    status: string
    /** 是否同步到国内 */
    synced_to_inland: boolean
}

/**
 * 历史记录项接口
 */
export interface HistoryRecord {
    /** 记录ID */
    id: string
    /** 任务ID */
    task_id: string
    /** 创建时间（毫秒） */
    create_time: number
    /** 消耗的积分/费用 */
    credits: number
    /** 总耗时（毫秒） */
    elapsed_time: number
    /** 请求方法 */
    method: string
    /** 模型名称 */
    model: string
    /** 状态 */
    status: HistoryRecordStatus
    /** 操作类型 */
    operation_type?: string
}

/**
 * 历史记录响应接口
 */
export interface HistoryRecordsResponse {
    /** 记录列表 */
    content: HistoryRecord[]
    /** 总条数 */
    total: number
}

/**
 * 历史记录请求参数
 */
export interface HistoryRecordsParams {
    /** 记录ID */
    id?: string
    /** 页码 */
    page: number
    /** 每页条数 */
    page_size: number
    /** 状态筛选 */
    status?: HistoryRecordStatus
}

/**
 * 商品信息接口
 */
export interface GoodsItem {
    /** 商品ID */
    id: number
    /** 商品名称 */
    name: string
    /** 价格（单位：分） */
    price: number
    /** 显示价格 */
    price_show: string
    /** 支付方式列表 */
    payment_types: string[]
}

/**
 * 套餐展示信息接口
 */
export interface PackageItem {
    /** 套餐ID */
    id: number
    /** 原始商品数据 */
    originalData: GoodsItem
    /** 显示价格 */
    price: string
    /** 积分数量（格式化后） */
    credits: string
    /** 单价 */
    unitPrice: string
}

/**
 * API定价响应接口
 */
export interface PricingResponse {
    /** 积分货架 */
    credits_shelf?: {
        /** 商品列表 */
        goods?: GoodsItem[]
    }
}

// ==================== API 请求函数 ====================

/**
 * 获取API余额（需要登录）
 * @returns Promise<number> 返回余额数字
 */
export const getApiBalanceApi = (): Promise<any> => {
    return handleAuthRequired(http.get('/api/v1/auth/user/balance'))
}

/**
 * 获取API定价信息（积分套餐）
 * 注：此接口不需要登录，不做拦截
 * @returns Promise<PricingResponse>
 */
export const getApiPricingApi = (): Promise<any> => {
    return http.get('/api/auth/h5/open/admin/pricing')
}

/**
 * 获取生成历史记录（需要登录）
 * @param params - 查询参数
 * @returns Promise<HistoryRecordsResponse>
 */
export const getGenerationRecordsApi = (
    params: Record<string, unknown>
): Promise<any> => {
    return handleAuthRequired(
        http.get<any>('/api/auth/h5/open/admin/generation/records', params)
    )
}

/**
 * 获取模型消耗费率
 * @returns Promise<any>
 */
export const getModelRatesApi = (): Promise<any> => {
    return http.get('/api/auth/h5/open/admin/modelRate')
}

// ==================== API Key 管理 ====================

/**
 * API Key 状态枚举
 */
export type ApiKeyStatus = 'ACTIVE' | 'INACTIVE' | 'DELETED'

/**
 * API Key 数据结构
 */
export interface ApiKeyItem {
    /** API 密钥 */
    api_key: string
    /** 创建时间戳 */
    create_time: number
    /** 密钥描述 */
    desc: string
    /** 密钥状态 */
    status: ApiKeyStatus
}

/**
 * API Key 分页数据
 */
export interface ApiKeyPage {
    content: ApiKeyItem[]
    page: number
    page_size: number
    total_page: number
    total: number
}

export interface ApiKeyPageResponse {
    success: boolean
    status: number
    data: ApiKeyPage
    message?: string
}

/**
 * 创建 API Key 请求参数
 */
export interface CreateApiKeyParams {
    /** 密钥描述 */
    desc?: string
    /** 密钥状态 */
    status?: ApiKeyStatus
    [key: string]: unknown
}

/**
 * 创建 API Key（需要登录）
 * @param params 创建参数
 * @returns Promise
 */
export const createApiKeyApi = (params: CreateApiKeyParams): Promise<any> => {
    return handleAuthRequired(
        http.post('/api/v1/auth/user/api-keys', params)
    )
}

/**
 * 分页获取用户 API Keys（需要登录）
 * @param params 分页参数
 * @returns Promise
 */
export const getApiKeysApi = (params: {
    page: number
    page_size: number
}): Promise<ApiKeyPageResponse> => {
    return handleAuthRequired<ApiKeyPageResponse>(
        http.get('/api/v1/auth/user/api-keys', params)
    )
}

/**
 * 删除 API Key（需要登录）
 * @param api_key 要删除的 API Key
 * @returns Promise
 */
export const deleteApiKeyApi = (api_key: string): Promise<any> => {
    return handleAuthRequired(
        http.delete('/api/v1/auth/user/api-keys', undefined, { api_key })
    )
}

/**
 * 获取落地页灵感作品列表
 * @returns Promise<InspirationWorkItem[]>
 */
export const getInspirationWorksList = async (): Promise<{
    status: number
    data: InspirationWorkItem[]
}> => {
    return http.get('/api/v1/landing/page/his-inspiration/list')
}
